import {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import 'rc-slider/assets/index.css';
import DataTable from 'react-data-table-component';
import stableStringify from 'fast-json-stable-stringify';

import ProviderSourceSelect from '../WizardBasics/ProviderSourceSelect/ProviderSourceSelect';
import ScreenPublisherList from './Filters/ScreenPublisherList/ScreenPublisherList';
import ScreenTypeList from './Filters/ScreenTypesList/ScreenTypesList';
import ScreenTierList from './Filters/ScreenTierList/ScreenTierList';
import ShowSelectedScreens from './Filters/ShowSelectedScreens/ShowSelectedScreens';
import FilterByPoi from './Filters/FilterByPoi/FilterByPoi';
import { MaxCpmInput } from './Filters/MaxCpmInput/MaxCpmInput';
import Search from './Filters/Search/Search';
import ImportScreens from '../../ImportScreens/ImportScreens';
import VenueTypeList from './Filters/VenueTypeList/VenueTypeList';
import RotationList from './Filters/RotationList/RotationList';
import SlotDurationList from './Filters/SlotDurationList/SlotDurationList';
import DownloadScreensToCSV from '../WizardReview/Blocks/DownloadScreensToCSV';

import Icon from '../../Icon/Icon';
import PropTypes from 'prop-types';

import CopyText from '../../CopyText/CopyText';
import Hint from '../../Tools/Hint';
import { getVenueTypesList } from '../../../services/venueTypesService';
import {
    formatNumber,
    getCpmMax,
    getWeeklyImpacts,
    getCostPerSpot,
    getScreenDisplayName,
} from '../../../utils/functions';
import {
    buildVenueTypeEntries,
    buildVenueTypeLabelLookup,
    extractVenueTypeLabel,
    normalizeVenueTypeValue,
} from '../../../utils/venueTypeLabels';
import { useAuth } from '../../../containers/UseAuth';
import { RequiredTag } from '../../Input/RequiredTag';

const getGalleryImages = (row) => {
    if (!Array.isArray(row?.gallery)) return [];
    return row.gallery.flatMap((g) =>
        Array.isArray(g?.gallery_items)
            ? g.gallery_items.map((it) => it?.image?.url).filter(Boolean)
            : []
    );
};

const getPublisherName = (row) =>
    row.publisher && typeof row.publisher === 'object'
        ? row.publisher.name
        : row.publisher ?? '-';

const PRIMARY_IDENTIFIER_FIELDS = [
    'external_id',
    'externalId',
    'key',
    'id',
];

function calculateHeight() {
    const winSize = useWindowSize();
    return winSize.height - 360 + 'px';
}

function useWindowSize() {
    const [windowSize, setWindowSize] = useState({
        width: undefined,
        height: undefined,
    });
    useEffect(() => {
        function handleResize() {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        }
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []); // Empty array ensures that effect is only run on mount
    return windowSize;
}

const getScreenIdentifierValues = (row) => {
    if (!row || typeof row !== 'object') return [];
    const values = [];

    const addValue = (value) => {
        if (value === null || value === undefined) return;
        const stringValue =
            typeof value === 'string' ? value.trim() : String(value);
        if (!stringValue) return;
        if (!values.includes(stringValue)) values.push(stringValue);
    };

    const providerId =
        typeof row?.provider === 'object' ? row?.provider?.id : undefined;
    const rawId =
        row?.id !== null && row?.id !== undefined ? row?.id : undefined;

    if (
        providerId &&
        rawId !== undefined &&
        rawId !== null &&
        String(rawId).trim()
    ) {
        const rawIdString = String(rawId).trim();
        if (!rawIdString.startsWith(`${providerId}_`)) {
            addValue(`${providerId}_${rawIdString}`);
        }
    }

    for (const field of PRIMARY_IDENTIFIER_FIELDS) {
        const value = row[field];
        addValue(value);
    }

    return values;
};

const getScreenIdentifier = (row) => {
    const identifiers = getScreenIdentifierValues(row);
    if (identifiers.length > 0) return identifiers[0];
    return null;
};

const normalizeScreenIdentifier = (value) =>
    typeof value === 'string' || typeof value === 'number'
        ? String(value).trim().toLowerCase()
        : '';

const getNormalizedScreenIdentifiers = (screen) =>
    getScreenIdentifierValues(screen)
        .map((identifier) => normalizeScreenIdentifier(identifier))
        .filter((identifier) => Boolean(identifier));

const getFirstPrice = (row, selectedCurrency) => {
    const price = row?.deals?.prices?.[0]?.price;
    if (typeof price === 'number') return price;

    const cpm = Number(row?.deals?.[selectedCurrency]?.CPM?.max_cpm);
    return Number.isFinite(cpm) ? cpm : 0;
};

const hasAudienceMeasurement = (row) => {
    if (typeof row?.hasAudience === 'boolean') {
        return row.hasAudience;
    }

    if (typeof row?.hasAudience === 'number') {
        return row.hasAudience > 0;
    }

    if (typeof row?.hasAudience === 'string') {
        const normalized = row.hasAudience.trim().toLowerCase();
        if (normalized === 'true' || normalized === '1') return true;
        if (normalized === 'false' || normalized === '0') return false;
    }

    return getWeeklyImpacts(row) > 0;
};

const normalizeRotationValue = (value) => {
    if (value === null || value === undefined) return '';
    const normalized = String(value).trim().toLowerCase();
    return normalized.endsWith('°') ? normalized.slice(0, -1) : normalized;
};

const normalizeSlotDurationValue = (value) => {
    if (value === null || value === undefined) return '';
    const normalized = String(value).trim().toLowerCase();
    return normalized.endsWith('s') ? normalized.slice(0, -1) : normalized;
};

const normalizeVenueValue = (value) => {
    if (value === null || value === undefined) return '';
    return String(value).trim();
};

const normalizeScreenTypeValue = (value) => {
    if (value === null || value === undefined) return '';
    const normalized = String(value).trim();
    if (!normalized) return '';
    return normalized.replace(/\s+/g, '_').toUpperCase();
};

const toFiniteNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const haversineDistanceMeters = (lat1, lon1, lat2, lon2) => {
    const R = 6371000;
    const toRad = (deg) => (deg * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(lat1)) *
            Math.cos(toRad(lat2)) *
            Math.sin(dLon / 2) *
            Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
};

const extractScreenCoordinates = (screen) => {
    const lat =
        toFiniteNumber(screen?.geo?.lat) ??
        toFiniteNumber(screen?.lat) ??
        null;
    const lon =
        toFiniteNumber(screen?.geo?.lon) ??
        toFiniteNumber(screen?.lng) ??
        null;
    if (lat === null || lon === null) return null;
    return { lat, lon };
};


const stableSerialize = (value) =>
    JSON.stringify(stableStringify(value));

const getScreenSortValue = (screen, orderBy) => {
    if (!screen) return '';

    if (orderBy === 'size') {
        const width = Number(screen.width);
        const height = Number(screen.height);
        if (Number.isFinite(width) && Number.isFinite(height)) {
            return width * height;
        }
        return String(screen.size ?? '');
    }

    if (orderBy === 'externalId') {
        return screen.externalId ?? screen.external_id ?? screen.id ?? '';
    }

    if (orderBy === 'publisherName') {
        return getPublisherName(screen);
    }

    if (orderBy === 'weeklyImpacts') {
        return getWeeklyImpacts(screen);
    }

    return screen[orderBy] ?? screen.alias ?? screen.name ?? '';
};

const sortScreens = (screens = [], orderBy = 'name', order = 'asc') => {
    if (!Array.isArray(screens)) return [];

    const direction = order === 'desc' ? -1 : 1;
    return [...screens].sort((a, b) => {
        if (orderBy === 'size') {
            const aWidth = Number(a?.width);
            const bWidth = Number(b?.width);
            if (Number.isFinite(aWidth) && Number.isFinite(bWidth)) {
                const widthCompare = aWidth - bWidth;
                if (widthCompare !== 0) return widthCompare * direction;
            }

            const aHeight = Number(a?.height);
            const bHeight = Number(b?.height);
            if (Number.isFinite(aHeight) && Number.isFinite(bHeight)) {
                const heightCompare = aHeight - bHeight;
                if (heightCompare !== 0) return heightCompare * direction;
            }
        }

        const aValue = getScreenSortValue(a, orderBy);
        const bValue = getScreenSortValue(b, orderBy);

        if (typeof aValue === 'number' && typeof bValue === 'number') {
            return (aValue - bValue) * direction;
        }

        return (
            String(aValue).localeCompare(String(bValue), undefined, {
                numeric: true,
                sensitivity: 'base',
            }) * direction
        );
    });
};

const getScreenKey = (screen) =>
    screen?.id ??
    screen?.external_id ??
    screen?.externalId ??
    screen?.key ??
    screen?.label ??
    screen?.name ??
    null;

const shouldUpdateSidebarBase = (currentScreens, nextScreens) => {
    if (!Array.isArray(nextScreens)) return false;
    const nextLength = nextScreens.length;
    const currentLength = Array.isArray(currentScreens)
        ? currentScreens.length
        : 0;

    if (currentLength !== nextLength) return true;
    for (let index = 0; index < nextLength; index += 1) {
        const currentKey = getScreenKey(currentScreens[index]);
        const nextKey = getScreenKey(nextScreens[index]);
        if (currentKey !== nextKey) return true;
    }
    return false;
};

const WizardScreens = forwardRef(function WizardScreens(
    {
        newCampaign,
        selectedCurrencyCode,
        isResizeMapOn,

        rows: rowsProp,
        totalRows: totalRowsProp = 0,
        page: pageProp = 1,
        perPage: perPageProp = 50,

        selectedScreens: selectedScreensProp = [],
        onChangeSelectedScreens = () => {},

        filters = {},

        onFiltersChange = () => {},
        onChangePage = () => {},
        onChangePerPage = () => {},

        userMargin = 0,
        loadingDataTable = false,
        isFetchingData = false,

        screenOpened,
        setScreenOpened,

        filteredScreensLocal = [],
        screensMatchingFilters = [],
        screenPassesFilters = null,
        screenPassesSelectionFilters = null,
        availableVenueTypeIds = [],
        availableVenueTypeCounts = [],
        availableRotations = [],
        availableRotationCounts = [],
        availableSlotDurations = [],
        availableSlotDurationCounts = [],
        availablePublisherIds = [],
        availablePublisherCounts = [],
        availableScreensForImport,
        tiers = [],
        screensState,
        setScreensState,
        filterScreenByPoi = false,
        setFilterScreenByPoi = () => {},
        selectedPoiZoneKeys = null,
        onPoiZoneKeysChange = () => {},
    },
    ref
) {
    const { t } = useTranslation();
    const auth = useAuth();
    const isAdminUser = auth?.isAdmin?.() ?? false;
    const isSupervisedUser = auth?.isSupervising?.() ?? false;
    const canFilterByPublisher = isAdminUser || isSupervisedUser;
    const canShowSensitiveColumns = isAdminUser && !isSupervisedUser;
    const isAdmin = auth?.isAdmin?.() ?? false;
    const isSupervising = auth?.isSupervising?.() ?? false;
    const canSelectProviders = auth?.session?.user?.cpm_mode === 1;

    const {
        providerSource = ['tgx'],
        q = '',
        orderBy = 'name',
        order = 'asc',
        selectedPublishers = [],
        selectedScreenTypes = [],
        selectedTiers = [],
        showSelectedScreens = false,
        maxCpm = null,
        isFilterByAudiences = false,
        selectedVenueTypes = [],
        selectedRotations = [],
        selectedSlotDurations = [],
    } = filters;

    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({
        selectedScreens: screensState.selectedScreens.length || false,
    });

    function validateScreens(selectedScreens) {
        const errors = {};
        if (!Array.isArray(selectedScreens) || selectedScreens.length === 0) {
            errors.selectedScreens = true; // o mensaje
        }
        return { isValid: Object.keys(errors).length === 0, errors };
    }

    //? Exponer método validate() al padre (createCampaignPage)
    useImperativeHandle(ref, () => ({
        validate() {
            const { isValid, errors: nextErrors } = validateScreens(
                screensState?.selectedScreens ?? []
            );
            setTouched((previous) =>
                previous.selectedScreens
                    ? previous
                    : { ...previous, selectedScreens: true }
            );
            setErrors((previous) =>
                Boolean(previous.selectedScreens) ===
                Boolean(nextErrors.selectedScreens)
                    ? previous
                    : nextErrors
            );
            return isValid;
        },
    }));

    const providerSourceValue = useMemo(() => {
        if (Array.isArray(providerSource)) return providerSource;
        return providerSource ? [providerSource] : [];
    }, [providerSource]);

    const rows = useMemo(() => {
        if (Array.isArray(rowsProp) && rowsProp.length) return rowsProp;
        if (Array.isArray(filteredScreensLocal) && filteredScreensLocal.length)
            return filteredScreensLocal;
        return Array.isArray(rowsProp) ? rowsProp : [];
    }, [rowsProp, filteredScreensLocal]);
    const hasSelectedScreens = Array.isArray(screensState?.selectedScreens)
        ? screensState.selectedScreens.length > 0
        : false;

    const screensForFilterSidebar = useMemo(() => {
        if (
            Array.isArray(screensMatchingFilters) &&
            screensMatchingFilters.length
        ) {
            return screensMatchingFilters;
        }
        if (
            Array.isArray(filteredScreensLocal) &&
            filteredScreensLocal.length
        ) {
            return filteredScreensLocal;
        }
        return Array.isArray(rows) ? rows : [];
    }, [screensMatchingFilters, filteredScreensLocal, rows]);

    const [sidebarBaseScreens, setSidebarBaseScreens] = useState(() => rows);
    const [filterCollapsed, setFilterCollapsed] = useState(false);
    const [venueTypesCatalog, setVenueTypesCatalog] = useState([]);

    useEffect(() => {
        let isMounted = true;

        const fetchVenueTypes = async () => {
            try {
                const response = await getVenueTypesList();
                if (!isMounted) return;
                setVenueTypesCatalog(response);
            } catch (error) {
                if (!isMounted) return;
                setVenueTypesCatalog([]);
                // eslint-disable-next-line no-console
                console.error('Failed to load venue types', error);
            }
        };

        fetchVenueTypes();

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        const noAdvancedFiltersSelected =
            (selectedVenueTypes?.length ?? 0) === 0 &&
            (selectedRotations?.length ?? 0) === 0 &&
            (selectedSlotDurations?.length ?? 0) === 0;

        const nextSidebarSource = Array.isArray(screensForFilterSidebar)
            ? screensForFilterSidebar
            : [];

        if (sidebarBaseScreens?.length === 0 || noAdvancedFiltersSelected) {
            if (
                shouldUpdateSidebarBase(sidebarBaseScreens, nextSidebarSource)
            ) {
                setSidebarBaseScreens([...nextSidebarSource]);
            }
        }
    }, [screensForFilterSidebar, sidebarBaseScreens, selectedVenueTypes?.length, selectedRotations?.length, selectedSlotDurations?.length]);

    const sidebarScreensSource = useMemo(() => {
        if (Array.isArray(sidebarBaseScreens) && sidebarBaseScreens.length) {
            return sidebarBaseScreens;
        }
        if (
            Array.isArray(filteredScreensLocal) &&
            filteredScreensLocal.length
        ) {
            return filteredScreensLocal;
        }
        if (Array.isArray(rowsProp) && rowsProp.length) return rowsProp;
        return rows;
    }, [sidebarBaseScreens, filteredScreensLocal, rowsProp, rows]);

    const venueTypeEntries = useMemo(
        () =>
            buildVenueTypeEntries({
                screens: sidebarScreensSource,
                venueTypesCatalog,
            }),
        [sidebarScreensSource, venueTypesCatalog]
    );

    const venueTypeLabelLookup = useMemo(
        () => buildVenueTypeLabelLookup(venueTypeEntries),
        [venueTypeEntries]
    );

    const normalizedSelectedScreenTypes = useMemo(
        () =>
            Array.isArray(selectedScreenTypes)
                ? Array.from(
                      new Set(
                          selectedScreenTypes
                              .map((value) => {
                                  if (typeof value === 'string') {
                                      return normalizeScreenTypeValue(value);
                                  }
                                  if (value && typeof value === 'object') {
                                      return normalizeScreenTypeValue(
                                          value.value ??
                                              value.name ??
                                              value.type ??
                                              value.id
                                      );
                                  }
                                  return '';
                              })
                              .filter(Boolean)
                      )
                  )
                : [],
        [selectedScreenTypes]
    );

    const totalRows =
        typeof totalRowsProp === 'number' ? totalRowsProp : rows.length;
    const page = typeof pageProp === 'number' ? pageProp : 1;
    const perPage = typeof perPageProp === 'number' ? perPageProp : 50;
    const selectedScreens = Array.isArray(selectedScreensProp)
        ? selectedScreensProp
        : [];
    const selectedScreensRef = useRef(selectedScreens);

    useEffect(() => {
        selectedScreensRef.current = selectedScreens;
    }, [selectedScreens]);

    const resolveScreenKey = useCallback((screen) => {
        if (!screen) return null;
        const primary = getScreenIdentifier(screen);
        if (primary !== null && primary !== undefined && primary !== '') {
            return String(primary);
        }
        if (screen?.__screenKey) return String(screen.__screenKey);
        const fallback = getScreenKey(screen);
        if (fallback !== null && fallback !== undefined && fallback !== '') {
            return String(fallback);
        }
        return null;
    }, []);

    const selectedScreenKeys = useMemo(() => {
        const keys = new Set();
        selectedScreens.forEach((screen) => {
            getNormalizedScreenIdentifiers(screen).forEach((identifier) =>
                keys.add(identifier)
            );
        });
        return keys;
    }, [selectedScreens]);

    const isScreenSelected = useCallback(
        (screen) => {
            const identifiers = getNormalizedScreenIdentifiers(screen);
            if (!Array.isArray(identifiers) || identifiers.length === 0)
                return false;
            return identifiers.some((identifier) =>
                selectedScreenKeys.has(identifier)
            );
        },
        [selectedScreenKeys]
    );

    const attachScreenKey = useCallback(
        (screen, index = 0) => {
            const key = resolveScreenKey(screen);
            if (key) {
                if (screen?.__screenKey === key) return screen;
                return { ...screen, __screenKey: key };
            }
            if (screen?.__screenKey) return screen;
            return { ...screen, __screenKey: `screen-${index}` };
        },
        [resolveScreenKey]
    );

    const rowsWithKeys = useMemo(() => {
        if (!Array.isArray(rows)) return [];
        return rows.map((row, index) => attachScreenKey(row, index));
    }, [rows, attachScreenKey]);

    const selectedRowsForDisplay = useMemo(() => {
        if (!showSelectedScreens) return [];

        const merged = [];
        const seenIdentifiers = new Set();
        const seenFallbackKeys = new Set();

        // Collect all selected screens without filtering — deduplication only.
        rowsWithKeys.forEach((row) => {
            if (!isScreenSelected(row)) return;
            const identifiers = getNormalizedScreenIdentifiers(row);
            if (identifiers.length) {
                if (
                    identifiers.some((identifier) =>
                        seenIdentifiers.has(identifier)
                    )
                )
                    return;
                identifiers.forEach((identifier) =>
                    seenIdentifiers.add(identifier)
                );
            } else {
                const key = resolveScreenKey(row);
                if (!key || seenFallbackKeys.has(key)) return;
                seenFallbackKeys.add(key);
            }
            merged.push(row);
        });

        selectedScreens.forEach((screen, index) => {
            const identifiers = getNormalizedScreenIdentifiers(screen);
            const fallbackKey = resolveScreenKey(screen);
            const alreadySeen = identifiers.length
                ? identifiers.some((identifier) =>
                      seenIdentifiers.has(identifier)
                  )
                : fallbackKey && seenFallbackKeys.has(fallbackKey);

            if (alreadySeen) return;

            const screenWithKey = attachScreenKey(
                screen,
                rowsWithKeys.length + index
            );

            if (identifiers.length) {
                identifiers.forEach((identifier) =>
                    seenIdentifiers.add(identifier)
                );
            } else {
                const keyToTrack =
                    fallbackKey ?? resolveScreenKey(screenWithKey);
                if (keyToTrack) {
                    seenFallbackKeys.add(keyToTrack);
                }
            }

            merged.push(screenWithKey);
        });

        // Apply sidebar filters after collecting and deduplicating.
        let filtered =
            typeof screenPassesSelectionFilters === 'function'
                ? merged.filter(screenPassesSelectionFilters)
                : merged;

        // Apply POI filter client-side when active in "show selected" mode.
        if (filterScreenByPoi) {
            const poiEntries = [];
            const zones = Array.isArray(newCampaign?.location?.zones)
                ? newCampaign.location.zones
                : [];
            zones.forEach((zone) => {
                if (zone?.active !== true && zone?.active !== 1) return;
                if (!Array.isArray(zone?.results)) return;
                zone.results.forEach((result) => {
                    const lat = toFiniteNumber(result?.lat);
                    const lon = toFiniteNumber(result?.lng);
                    const radius = toFiniteNumber(result?.radius);
                    if (lat === null || lon === null || radius === null || radius <= 0)
                        return;
                    poiEntries.push({ lat, lon, radius });
                });
            });
            if (poiEntries.length > 0) {
                filtered = filtered.filter((screen) => {
                    const coords = extractScreenCoordinates(screen);
                    if (!coords) return false;
                    return poiEntries.some(
                        (poi) =>
                            haversineDistanceMeters(
                                coords.lat,
                                coords.lon,
                                poi.lat,
                                poi.lon
                            ) <= poi.radius
                    );
                });
            }
        }

        return filtered;
    }, [
        attachScreenKey,
        filterScreenByPoi,
        isScreenSelected,
        newCampaign,
        resolveScreenKey,
        rowsWithKeys,
        screenPassesSelectionFilters,
        selectedScreens,
        showSelectedScreens,
    ]);

    const userSelectionIntentRef = useRef(false);

    const markSelectionIntentFromEvent = useCallback((event) => {
        const target = event?.target;
        if (
            target?.tagName === 'INPUT' &&
            target?.getAttribute('type') === 'checkbox'
        ) {
            userSelectionIntentRef.current = true;
        }
    }, []);

    const tableData = useMemo(() => {
        if (showSelectedScreens) {
            return sortScreens(selectedRowsForDisplay, orderBy, order);
        }
        return rowsWithKeys;
    }, [order, orderBy, rowsWithKeys, selectedRowsForDisplay, showSelectedScreens]);

    const effectiveSidebarSource = showSelectedScreens
        ? selectedRowsForDisplay
        : sidebarScreensSource;
    const effectiveVenueTypeIds = showSelectedScreens ? [] : availableVenueTypeIds;
    const effectiveVenueTypeCounts = showSelectedScreens ? [] : availableVenueTypeCounts;
    const effectiveRotations = showSelectedScreens ? [] : availableRotations;
    const effectiveRotationCounts = showSelectedScreens ? [] : availableRotationCounts;
    const effectiveSlotDurations = showSelectedScreens ? [] : availableSlotDurations;
    const effectiveSlotDurationCounts = showSelectedScreens ? [] : availableSlotDurationCounts;

    // Publisher counts: delegated to ScreenPublisherList via screensForCounts
    // so it can match screen.publisher (name string) against its loaded publishers list.
    const effectivePublisherIds = showSelectedScreens ? [] : availablePublisherIds;
    const effectivePublisherCounts = showSelectedScreens ? [] : availablePublisherCounts;

    const activePoiEntries = useMemo(() => {
        const zones = Array.isArray(newCampaign?.location?.zones)
            ? newCampaign.location.zones
            : [];
        const entries = [];

        zones.forEach((zone) => {
            const isActive = zone?.active === true || zone?.active === 1;
            if (!isActive || !Array.isArray(zone?.results)) return;

            zone.results.forEach((result) => {
                const lat = toFiniteNumber(result?.lat);
                const lon = toFiniteNumber(result?.lng);
                const radius = toFiniteNumber(result?.radius);
                if (lat === null || lon === null || radius === null) return;
                if (radius <= 0) return;

                entries.push({
                    label: String(zone?.search || zone?.label || 'POI'),
                    color: result?.color || '#2563eb',
                    lat,
                    lon,
                    radius,
                });
            });
        });

        return entries;
    }, [newCampaign?.location?.zones]);

    const activePoiZones = useMemo(() => {
        const zones = Array.isArray(newCampaign?.location?.zones)
            ? newCampaign.location.zones
            : [];
        return zones
            .map((zone, index) => {
                const label = String(zone?.search || zone?.label || 'POI');
                const validResults = (
                    Array.isArray(zone?.results) ? zone.results : []
                ).filter((r) => {
                    const lat = toFiniteNumber(r?.lat);
                    const lng = toFiniteNumber(r?.lng);
                    const radius = toFiniteNumber(r?.radius);
                    return (
                        lat !== null &&
                        lng !== null &&
                        radius !== null &&
                        radius > 0
                    );
                });
                return {
                    key: `${index}:${label}`,
                    label,
                    color: validResults[0]?.color || '#2563eb',
                    locationCount: validResults.length,
                    isActive:
                        zone?.active === true || zone?.active === 1,
                };
            })
            .filter((z) => z.locationCount > 0);
    }, [newCampaign?.location?.zones]);

    const screenPoiMatchesByKey = useMemo(() => {
        const matchesByKey = new Map();
        if (!activePoiEntries.length) return matchesByKey;

        tableData.forEach((screen) => {
            const key = resolveScreenKey(screen) || screen?.__screenKey;
            if (!key) return;

            const coordinates = extractScreenCoordinates(screen);
            if (!coordinates) {
                matchesByKey.set(key, []);
                return;
            }

            const matches = [];
            const seen = new Set();

            activePoiEntries.forEach((poi) => {
                const distance = haversineDistanceMeters(
                    coordinates.lat,
                    coordinates.lon,
                    poi.lat,
                    poi.lon
                );
                if (distance > poi.radius) return;

                const poiKey = `${poi.label}|${poi.color}`;
                if (seen.has(poiKey)) return;
                seen.add(poiKey);
                matches.push({
                    label: poi.label,
                    color: poi.color,
                });
            });

            matchesByKey.set(key, matches);
        });

        return matchesByKey;
    }, [activePoiEntries, tableData, resolveScreenKey]);

    const getPoiMatchesForRow = useCallback(
        (row) => {
            const key = resolveScreenKey(row) || row?.__screenKey;
            if (!key) return [];
            return screenPoiMatchesByKey.get(key) || [];
        },
        [screenPoiMatchesByKey, resolveScreenKey]
    );

    const selectedCurrencyShortName =
        selectedCurrencyCode ||
        newCampaign?.selectedCurrency?.short_name;
    const budget = newCampaign?.budget ?? {};
    const cpm = budget.cpm;
    const isCpmOptimized = budget.cpmOptimizer;

    const { control, watch, reset, setValue } = useForm({
        defaultValues: {
            providerSource: providerSourceValue,
            q,
            selectedPublishers: canFilterByPublisher ? selectedPublishers : [],
            selectedScreenTypes: normalizedSelectedScreenTypes,
            selectedTiers,
            showSelectedScreens,
            maxCpm,
            isFilterByAudiences,
            selectedVenueTypes,
            selectedRotations,
            selectedSlotDurations,
        },
    });

    const selectedScreenTypesValue = watch('selectedScreenTypes') ?? [];
    const rawSelectedPublishersValue = watch('selectedPublishers') ?? [];
    const selectedPublishersValue = canFilterByPublisher
        ? rawSelectedPublishersValue
        : [];
    const selectedTiersValue = watch('selectedTiers') ?? [];
    const selectedVenueTypesValue = watch('selectedVenueTypes') ?? [];
    const selectedRotationsValue = watch('selectedRotations') ?? [];
    const selectedSlotDurationsValue = watch('selectedSlotDurations') ?? [];

    useEffect(() => {
        reset({
            providerSource: providerSourceValue,
            q,
            selectedPublishers: canFilterByPublisher ? selectedPublishers : [],
            selectedScreenTypes: normalizedSelectedScreenTypes,
            selectedTiers,
            showSelectedScreens,
            maxCpm,
            isFilterByAudiences,
            selectedVenueTypes,
            selectedRotations,
            selectedSlotDurations,
        });
    }, [providerSourceValue, q, selectedPublishers, normalizedSelectedScreenTypes, selectedTiers, showSelectedScreens, maxCpm, isFilterByAudiences, selectedVenueTypes, selectedRotations, selectedSlotDurations, reset, canFilterByPublisher]);

    const serializedFiltersRef = useRef(stableSerialize(filters));

    useEffect(() => {
        serializedFiltersRef.current = stableSerialize(filters);
    }, [filters]);

    useEffect(() => {
        const subscription = watch((value) => {
            const sanitized = {
                ...value,
                orderBy,
                order,
                selectedScreenTypes: Array.isArray(value.selectedScreenTypes)
                    ? Array.from(
                          new Set(
                              value.selectedScreenTypes
                                  .map((val) => {
                                      if (typeof val === 'string') {
                                          return normalizeScreenTypeValue(val);
                                      }
                                      if (val && typeof val === 'object') {
                                          return normalizeScreenTypeValue(
                                              val.value ??
                                                  val.name ??
                                                  val.type ??
                                                  val.id
                                          );
                                      }
                                      return '';
                                  })
                                  .filter(Boolean)
                          )
                      )
                    : [],
                selectedPublishers:
                    canFilterByPublisher &&
                    Array.isArray(value.selectedPublishers)
                        ? value.selectedPublishers.filter(
                              (publisher, index, arr) => {
                                  if (!publisher || publisher.id == null)
                                      return false;
                                  const firstIndex = arr.findIndex(
                                      (item) => item?.id === publisher.id
                                  );
                                  return firstIndex === index;
                              }
                          )
                        : [],
                selectedRotations: Array.isArray(value.selectedRotations)
                    ? value.selectedRotations
                          .map((val) => normalizeRotationValue(val))
                          .filter(Boolean)
                    : [],
                selectedSlotDurations: Array.isArray(
                    value.selectedSlotDurations
                )
                    ? value.selectedSlotDurations
                          .map((val) => normalizeSlotDurationValue(val))
                          .filter(Boolean)
                    : [],
                selectedVenueTypes: Array.isArray(value.selectedVenueTypes)
                    ? value.selectedVenueTypes
                          .map((val) => normalizeVenueValue(val))
                          .filter(Boolean)
                    : [],
                isFilterByAudiences: Boolean(
                    value.isFilterByAudiences ?? isFilterByAudiences
                ),
            };
            const serialized = stableSerialize(sanitized);
            if (serialized !== serializedFiltersRef.current) {
                serializedFiltersRef.current = serialized;
                onFiltersChange?.(sanitized);
            }
        });
        return () => subscription.unsubscribe();
    }, [
        watch,
        onFiltersChange,
        canFilterByPublisher,
        isFilterByAudiences,
        orderBy,
        order,
    ]);

    const pageRef = useRef(page);
    pageRef.current = page;

    const [paginationReset, setPaginationReset] = useState(false);

    const handleSort = useCallback(
        (column, sortDirection) => {
            const nextOrderBy = column?.id || orderBy || 'name';
            const pageToRestore = pageRef.current;
            onFiltersChange?.({
                ...filters,
                orderBy: nextOrderBy,
                order: sortDirection,
            });

            onChangePage?.(pageToRestore);
            setPaginationReset((prev) => !prev);
        },
        [filters, onFiltersChange, orderBy, onChangePage]
    );

    // Mantener estilos del original
    const styleLabel = { color: '#bdbdbd', fontSize: 10, marginTop: 0.5 };

    const getVenueTypeLabel = useCallback(
        (row) => {
            if (!row) return '-';
            const labels = [];

            const addLabelsFromValue = (value) => {
                if (!value) return;
                const candidates = Array.isArray(value) ? value : [value];
                candidates.forEach((candidate) => {
                    let primaryValue = candidate;
                    if (candidate && typeof candidate === 'object') {
                        primaryValue =
                            candidate.id ??
                            candidate.venue_type_id ??
                            candidate.value ??
                            candidate.code ??
                            candidate.name ??
                            candidate.label;
                    }

                    const normalized = normalizeVenueTypeValue(primaryValue);
                    if (normalized && venueTypeLabelLookup.has(normalized)) {
                        labels.push(venueTypeLabelLookup.get(normalized));
                        return;
                    }

                    const fallbackLabel =
                        extractVenueTypeLabel(candidate) ||
                        (primaryValue !== null &&
                        primaryValue !== undefined
                            ? String(primaryValue).trim()
                            : '');
                    if (fallbackLabel) labels.push(fallbackLabel);
                });
            };

            addLabelsFromValue(row?.venue_type);
            addLabelsFromValue(row?.venueType);
            addLabelsFromValue(row?.venue_type_id);

            const uniqueLabels = Array.from(new Set(labels.filter(Boolean)));
            if (uniqueLabels.length === 0) return '-';
            return uniqueLabels.join(', ');
        },
        [venueTypeLabelLookup]
    );

    const titleWarning = (row, toShowWarning) => {
        if (
            !isCpmOptimized &&
            getFirstPrice(row, selectedCurrencyShortName) > cpm
        ) {
            return t(
                'sections.campaigns.new-campaign.wizard-screens.screens.cpm-low'
            );
        } else {
            return toShowWarning ? false : getScreenDisplayName(row);
        }
    };

    const handleScreenDetails = (screen) => {
        const imgs = getGalleryImages(screen);
        setScreenOpened?.({ ...screen, galleryImages: imgs });
    };

    const calculateEcpm = (row, selectedCurrency) => {
        if (!row) {
            return 0;
        }

        const denominator = 1 - userMargin;
        const safeDenominator = denominator === 0 ? 1 : denominator;
        const cpmFromDeals = Number(
            row?.deals?.[selectedCurrency]?.['CPM']?.max_cpm
        );

        if (Number.isFinite(cpmFromDeals) && cpmFromDeals > 0) {
            return cpmFromDeals / safeDenominator;
        }

        if (typeof row.ecpm === 'number' && !Number.isNaN(row.ecpm)) {
            return row.ecpm / safeDenominator;
        }

        return 0;
    };

    const handleSelectedRowsChange = useCallback(
        ({ selectedRows }) => {
            const currentSelectedScreens = selectedScreensRef.current;
            if (
                !userSelectionIntentRef.current &&
                Array.isArray(selectedRows) &&
                currentSelectedScreens.length > 0 &&
                selectedRows.length <= currentSelectedScreens.length
            ) {
                return;
            }

            userSelectionIntentRef.current = false;

            const currentRowIdentifiers = new Set();
            const currentRowFallbackKeys = new Set();

            tableData.forEach((row) => {
                const identifiers = getNormalizedScreenIdentifiers(row);
                if (identifiers.length) {
                    identifiers.forEach((identifier) =>
                        currentRowIdentifiers.add(identifier)
                    );
                } else {
                    const key = resolveScreenKey(row);
                    if (key) currentRowFallbackKeys.add(key);
                }
            });

            const preservedSelections = currentSelectedScreens
                .filter((screen) => {
                    const identifiers = getNormalizedScreenIdentifiers(screen);
                    if (identifiers.length) {
                        return !identifiers.some((identifier) =>
                            currentRowIdentifiers.has(identifier)
                        );
                    }
                    const key = resolveScreenKey(screen);
                    return key && !currentRowFallbackKeys.has(key);
                })
                .map((screen, index) => attachScreenKey(screen, index));

            const merged = [...preservedSelections];
            const identityToIndex = new Map();
            const fallbackKeyToIndex = new Map();

            merged.forEach((screen, index) => {
                const identifiers = getNormalizedScreenIdentifiers(screen);
                if (identifiers.length) {
                    identifiers.forEach((identifier) => {
                        if (!identityToIndex.has(identifier)) {
                            identityToIndex.set(identifier, index);
                        }
                    });
                } else {
                    const key = resolveScreenKey(screen);
                    if (key && !fallbackKeyToIndex.has(key)) {
                        fallbackKeyToIndex.set(key, index);
                    }
                }
            });

            selectedRows.forEach((row) => {
                const identifiers = getNormalizedScreenIdentifiers(row);
                let targetIndex = -1;

                if (identifiers.length) {
                    for (const identifier of identifiers) {
                        if (identityToIndex.has(identifier)) {
                            targetIndex = identityToIndex.get(identifier);
                            break;
                        }
                    }
                }

                if (targetIndex === -1) {
                    const key = resolveScreenKey(row);
                    if (key && fallbackKeyToIndex.has(key)) {
                        targetIndex = fallbackKeyToIndex.get(key);
                    }
                }

                const rowWithKey = attachScreenKey(row, merged.length);

                if (targetIndex !== -1) {
                    merged[targetIndex] = rowWithKey;
                } else {
                    targetIndex = merged.push(rowWithKey) - 1;
                }

                if (identifiers.length) {
                    identifiers.forEach((identifier) =>
                        identityToIndex.set(identifier, targetIndex)
                    );
                } else {
                    const key = resolveScreenKey(rowWithKey);
                    if (key) fallbackKeyToIndex.set(key, targetIndex);
                }
            });

            setTouched((p) => {
                const nextTouched = { ...p, selectedScreens: true };

                // solo validás si ya fue touched (o si querés que aparezca apenas toque el datatable, dejalo siempre true)
                if (nextTouched.selectedScreens) {
                    setErrors(validateScreens(merged).errors);
                }

                return nextTouched;
            });

            onChangeSelectedScreens?.(merged);
        },
        [
            attachScreenKey,
            onChangeSelectedScreens,
            resolveScreenKey,
            tableData,
        ]
    );

    const allColumns = useMemo(
        () => [
            {
                id: 'name',
                name: (
                    <button>
                        {t(
                            'sections.campaigns.new-campaign.wizard-screens.screens.table-screenName'
                        )}
                    </button>
                ),
                selector: (row) => getScreenDisplayName(row),
                sortable: true,
                cell: (row) => {
                    const screenIdentifier = getScreenIdentifier(row);
                    const hasIdentifier = Boolean(
                        normalizeScreenIdentifier(screenIdentifier)
                    );
                    const identifierLabel = hasIdentifier
                        ? String(screenIdentifier).toUpperCase()
                        : null;
                    const showAudienceWarning = !hasAudienceMeasurement(row);
                    const poiMatches = getPoiMatchesForRow(row);

                    return (
                        <div
                            className="w-full truncate"
                            title={titleWarning(row, false)}>
                            {titleWarning(row, true) && (
                                <div
                                    className="absolute "
                                    style={{ right: '-2%', bottom: '29%' }}>
                                    <Icon
                                        name="exclamation-triangle"
                                        fixedWidth
                                        className="text-red-300"
                                    />
                                </div>
                            )}
                            <div className="inline-flex">
                                {identifierLabel && (
                                    <span
                                        className="inline-block bg-gray-200 p-1 mr-1 mb-1 rounded-sm"
                                        style={{
                                            fontSize: '.8em',
                                            padding: '1px .4em 1px .4em',
                                        }}>
                                        <CopyText text={identifierLabel} />
                                    </span>
                                )}
                                {row.screenRequest === 0 && (
                                    <span
                                        title={t(
                                            'common.tooltips.screens.without-requests'
                                        )}
                                        style={{
                                            cursor: 'default',
                                            position: 'relative',
                                        }}
                                        >
                                        <div className="rounded-full w-3 h-3 group mr-[-18px] flex items-center justify-center bg-red-500"></div>
                                    </span>
                                )}
                                {row.screenRequest === 1 && (
                                    <span
                                        title={t(
                                            'common.tooltips.screens.low-requests'
                                        )}
                                        style={{
                                            cursor: 'default',
                                            position: 'relative',
                                        }}
                                        >
                                        <div className="rounded-full w-3 h-3 group mr-[-18px] flex items-center justify-center bg-yellow-400"></div>
                                    </span>
                                )}
                                {row.adType === 'VAST' && (
                                    <span
                                        title={t(
                                            'common.tooltips.screens.vast'
                                        )}
                                        className="inline-block bg-gray-200 p-1 mr-1 mb-1 rounded-sm text-blue-400"
                                        style={{
                                            fontSize: '.8em',
                                            padding: '1px .4em 1px .4em',
                                        }}>
                                        <strong>VAST</strong>
                                    </span>
                                )}
                                {showAudienceWarning && (
                                    <Hint
                                        title={t(
                                            'sections.campaigns.new-campaign.wizard-basics.timing.average-impacts-measurement-title'
                                        )}
                                        type="warning"
                                        text={t(
                                            'sections.campaigns.new-campaign.wizard-screens.screens.screens-without-impacts-label'
                                        )}
                                        icon={'exclamation-triangle'}
                                        className="ml-1"
                                        dataFor={'no-impacts'}
                                    />
                                )}
                                {poiMatches.length > 0 && (
                                    <span className="inline-flex items-center ml-2 gap-1">
                                        {poiMatches
                                            .slice(0, 4)
                                            .map((match, index) => (
                                                <span
                                                    key={`${match.label}-${index}`}
                                                    title={`POI: ${match.label}`}
                                                    data-tip={`POI: ${match.label}`}
                                                    data-for="poi-dot-tooltip"
                                                    className="inline-block w-2.5 h-2.5 rounded-full border border-white shadow-sm"
                                                    style={{
                                                        backgroundColor:
                                                            match.color,
                                                    }}
                                                />
                                            ))}
                                        {poiMatches.length > 4 && (
                                            <span
                                                className="text-xs text-gray-500"
                                                title={poiMatches
                                                    .slice(4)
                                                    .map((match) => match.label)
                                                    .join(', ')}>
                                                +{poiMatches.length - 4}
                                            </span>
                                        )}
                                    </span>
                                )}
                            </div>
                            <br />
                            {getScreenDisplayName(row)}
                        </div>
                    );
                },
                width: '40ch',
            },
            {
                id: 'venueType',
                name: t(
                    'sections.campaigns.new-campaign.wizard-screens.screens.table-venueType',
                    'Venue Type'
                ),
                selector: (row) => getVenueTypeLabel(row),
                sortable: true,
                cell: (row) => {
                    const label = getVenueTypeLabel(row);
                    return (
                        <div className="truncate" title={label}>
                            {label}
                        </div>
                    );
                },
                width: '20ch',
            },
            {
                name: '',
                cell: (row) => (
                    <div className="flex items-center justify-center">
                        <button
                            type="button"
                            className="text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-full p-1 transition-colors"
                            onClick={() => handleScreenDetails(row)}
                            aria-label={t(
                                'sections.campaigns.new-campaign.wizard-screens.screens.open-details'
                            )}
                            title={t(
                                'sections.campaigns.new-campaign.wizard-screens.screens.open-details'
                            )}>
                            <Icon
                                name="info-circle"
                                fixedWidth
                                className="text-lg"
                            />
                        </button>
                    </div>
                ),
                sortable: false,
                width: '4ch',
            },
            {
                id: 'ecpm',
                name: (
                    <div id="ecpm-name" className="flex">
                        <Hint
                            text={t(
                                'sections.campaigns.new-campaign.wizard-screens.screens.ecpm-hint-description'
                            )}
                            type="info"
                            className="mr-1 mt-1"
                            dataFor={'ecpm-name'}
                        />
                        <span className="flex mt-1">
                            <b>{t('common.words.ecpm')}</b>{' '}
                            <b style={styleLabel}>
                                ({selectedCurrencyShortName})
                            </b>
                        </span>
                    </div>
                ),
                selector: (row) =>
                    calculateEcpm(row, selectedCurrencyShortName),
                sortable: true,
                right: true,
                cell: (row) => (
                    <div style={{ marginRight: '45px' }}>
                        {formatNumber(
                            calculateEcpm(row, selectedCurrencyShortName),
                            'properCurrency',
                            null,
                            { currency: selectedCurrencyShortName }
                        )}
                    </div>
                ),
                width: '21ch',
            },
            {
                id: 'weeklyImpacts',
                name: (
                    <div
                        id="impacts-name"
                        className="flex"
                        style={{ marginRight: '40px' }}>
                        <Hint
                            text={t(
                                'sections.campaigns.new-campaign.wizard-screens.screens.impacts-hint-description'
                            )}
                            type="info"
                            className="mr-1 mt-1"
                            dataFor={'impacts-name'}
                        />
                        <span className="mt-1">
                            <b>
                                {t(
                                    'sections.campaigns.new-campaign.wizard-screens.screens.table-screenWeeklyImpacts'
                                )}
                            </b>
                        </span>
                    </div>
                ),
                selector: (row) => getWeeklyImpacts(row),
                cell: (row) => (
                    <div style={{ marginRight: '10px' }}>
                        {formatNumber(getWeeklyImpacts(row), 'withcommas')}
                    </div>
                ),
                sortable: true,
                right: true,
            },
            {
                id: 'size',
                name: t(
                    'sections.campaigns.new-campaign.wizard-screens.screens.table-screenSize'
                ),
                selector: (row) => row.size,
                sortable: true,
                right: true,
                width: '15ch',
            },
            {
                id: 'spot-cost',
                name: (
                    <span>
                        <b>{t('common.components.ReportsTable.spot-cost')}</b>{' '}
                        <b style={styleLabel}>({selectedCurrencyShortName})</b>
                    </span>
                ),
                selector: (row) => {
                    const cps = getCostPerSpot(
                        row.deals,
                        selectedCurrencyShortName
                    );
                    return Number.isFinite(cps) && cps !== 0
                        ? cps / (1 - userMargin)
                        : null;
                },
                cell: (row) => {
                    const cps = getCostPerSpot(
                        row.deals,
                        selectedCurrencyShortName
                    );
                    return formatNumber(
                        cps / (1 - userMargin),
                        'properCurrency',
                        null,
                        {
                            currency: selectedCurrencyShortName,
                        }
                    );
                },
                sortable: true,
                right: true,
            },
            {
                id: 'publisher',
                name: t(
                    'sections.campaigns.new-campaign.wizard-screens.screens.table-screenPublisher'
                ),
                selector: (row) => getPublisherName(row),
                cell: (row) => (
                    <div title={getPublisherName(row)}>
                        {getPublisherName(row)}
                    </div>
                ),
                sortable: true,
                width: '19ch',
                omit: !isSupervising && !isAdmin,
            },
        ],
        [
            t,
            getVenueTypeLabel,
            userMargin,
            selectedCurrencyShortName,
            isCpmOptimized,
            cpm,
            getPoiMatchesForRow,
            isAdmin,
            isSupervising,
        ]
    );

    const columns = useMemo(() => {
        if (canShowSensitiveColumns) return allColumns;
        return allColumns.filter(
            (column) =>
                column?.id !== 'spot-cost' &&
                (column?.id !== 'publisher' || canFilterByPublisher)
        );
    }, [allColumns, canShowSensitiveColumns, canFilterByPublisher]);

    const screenOpenedKey = screenOpened
        ? resolveScreenKey(screenOpened)
        : null;

    const conditionalRowStyles = [
        {
            when: (row) =>
                !isCpmOptimized &&
                getFirstPrice(row, selectedCurrencyShortName) > cpm,
            style: { opacity: 0.55 },
        },
        {
            when: (row) => selectedScreens?.length && isScreenSelected(row),
            style: { backgroundColor: 'rgba(59, 130, 246, 0.17)' },
        },
        {
            when: (row) =>
                screenOpenedKey && resolveScreenKey(row) === screenOpenedKey,
            style: { backgroundColor: 'rgba(176, 215, 241, 0.15)' },
        },
    ];

    return (
        <>
            <div
                className={`mt-2 text-sm transition-transform duration-300 ${
                    isResizeMapOn
                        ? 'transform scale-75 -translate-y-20 -mr-32 -ml-16 transition-all '
                        : ''
                }
                    w-full
                    min-h-0
                    max-h-full
                    flex
                    flex-row
                    `}>
                {/* ==== Sidebar de filtros: mantiene la misma UI ==== */}
                {/** filter container */}
                <div
                    className={`mt-0 border-r max-h-full overflow-y-auto pt-6`}>
                    <div className="flex flex-row justify-between items-center">
                        <div className="title font-normal">
                            <button
                                onClick={() =>
                                    filterCollapsed &&
                                    setFilterCollapsed(!filterCollapsed)
                                }
                                className={`${
                                    !filterCollapsed ? 'cursor-text' : ''
                                }`}>
                                <Icon
                                    name="filter"
                                    className="mr-1"
                                />
                            </button>
                            {!filterCollapsed && (
                                <>
                                    {t(
                                        'sections.campaigns.new-campaign.wizard-screens.filters.filters-label'
                                    )}
                                </>
                            )}
                        </div>
                        <div>
                            {!filterCollapsed && (
                                <button
                                    onClick={() =>
                                        setFilterCollapsed(!filterCollapsed)
                                    }>
                                    <Icon
                                        name="compress-wide"
                                        size={12}
                                        className="mr-4"
                                    />
                                </button>
                            )}
                        </div>
                    </div>
                    <div
                        className={`divide-y flex flex-col ${
                            filterCollapsed ? 'hidden' : ''
                        }`}>
                        <div>
                            <div className="title font-light mt-4 w-40">
                                {t(
                                    'sections.campaigns.new-campaign.wizard-screens.filters.provider-source-label',
                                    'Provider source'
                                )}
                            </div>
                            <Controller
                                name="providerSource"
                                control={control}
                                render={({ field }) => (
                                    <ProviderSourceSelect
                                        value={field.value}
                                        disabled={!canSelectProviders}
                                        onChange={(vals) =>
                                            field.onChange(
                                                Array.isArray(vals) &&
                                                    vals.length
                                                    ? vals
                                                    : []
                                            )
                                        }
                                    />
                                )}
                            />
                        </div>

                        <Controller
                            name="showSelectedScreens"
                            control={control}
                            render={({ field }) => (
                                <ShowSelectedScreens
                                    showSelectedScreens={field.value}
                                    setShowSelectedScreens={() =>
                                        field.onChange(!field.value)
                                    }
                                />
                            )}
                        />

                        {/* <Controller
                                name="maxCpm"
                                control={control}
                                render={({ field }) => (
                                    <MaxCpmInput
                                        maxCpm={field.value}
                                        setMaxCpm={(val) => field.onChange(val)}
                                        currencySymbol={selectedCurrencySymbol}
                                    />
                                )}
                            /> */}

                        <FilterByPoi
                            filterScreenByPoi={filterScreenByPoi}
                            setFilterScreenByPoi={setFilterScreenByPoi}
                            activePoiZones={activePoiZones}
                            selectedPoiZoneKeys={selectedPoiZoneKeys}
                            onPoiZoneKeysChange={onPoiZoneKeysChange}
                        />

                        <ScreenTypeList
                            screens={rows}
                            screensFilteredFromScreensWizard={
                                effectiveSidebarSource
                            }
                            filteredScreensLocal={
                                showSelectedScreens
                                    ? selectedRowsForDisplay
                                    : screensForFilterSidebar
                            }
                            selectedTypes={selectedScreenTypesValue}
                            selectedPublishers={selectedPublishersValue}
                            isFilterByAudiences={isFilterByAudiences}
                            onChange={(val = []) =>
                                setValue('selectedScreenTypes', val, {
                                    shouldDirty: true,
                                })
                            }
                        />

                        {canFilterByPublisher ? (
                            <ScreenPublisherList
                                selectedPublishers={selectedPublishersValue}
                                availablePublisherIds={effectivePublisherIds}
                                availablePublisherCounts={effectivePublisherCounts}
                                screensForCounts={
                                    showSelectedScreens
                                        ? selectedRowsForDisplay
                                        : null
                                }
                                onChange={(val = []) =>
                                    setValue('selectedPublishers', val, {
                                        shouldDirty: true,
                                    })
                                }
                            />
                        ) : null}

                        <VenueTypeList
                            screens={rows}
                            allScreens={effectiveSidebarSource}
                            selectedVenueTypes={selectedVenueTypesValue}
                            isFilterByAudiences={isFilterByAudiences}
                            venueTypesCatalog={venueTypesCatalog}
                            availableVenueTypeIds={effectiveVenueTypeIds}
                            availableVenueTypeCounts={effectiveVenueTypeCounts}
                            onChange={(val) =>
                                setValue('selectedVenueTypes', val, {
                                    shouldDirty: true,
                                })
                            }
                        />

                        <RotationList
                            screens={rows}
                            allScreens={effectiveSidebarSource}
                            selectedRotations={selectedRotationsValue}
                            availableRotations={effectiveRotations}
                            availableRotationCounts={effectiveRotationCounts}
                            isFilterByAudiences={isFilterByAudiences}
                            onChange={(val) =>
                                setValue('selectedRotations', val, {
                                    shouldDirty: true,
                                })
                            }
                        />

                        <SlotDurationList
                            screens={rows}
                            allScreens={effectiveSidebarSource}
                            selectedSlotDurations={selectedSlotDurationsValue}
                            availableSlotDurations={effectiveSlotDurations}
                            availableSlotDurationCounts={effectiveSlotDurationCounts}
                            isFilterByAudiences={isFilterByAudiences}
                            onChange={(val) =>
                                setValue('selectedSlotDurations', val, {
                                    shouldDirty: true,
                                })
                            }
                        />

                    </div>
                </div>

                {/** Screens container */}
                <div
                    className={`rounded transition-all px-2 min-w-0 max-w-full w-full flex flex-col pt-5`}>
                    <div className="flex flex-col md:flex-row md:items-start justify-between content-center w-full gap-3">
                        <div className="title text-xl font-extralight min-w-0">
                            {t(
                                'sections.campaigns.new-campaign.wizard-screens.screens.screens-title'
                            )}
                            <ImportScreens
                                availableScreens={
                                    availableScreensForImport ?? rows
                                }
                                selectedScreensLocal={selectedScreens}
                                updateSelectedScreens={
                                    onChangeSelectedScreens
                                }
                            />
                        </div>

                        <div className="flex-shrink-0">
                            <Controller
                                name="q"
                                control={control}
                                render={({ field }) => (
                                    <Search
                                        value={field.value}
                                        onChange={(val) => field.onChange(val)}
                                    />
                                )}
                            />
                        </div>
                    </div>
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-2 mt-1">
                        <div className="min-w-0">
                            <div className="">
                                <span className="text-xs font-bold">
                                    {t(
                                        'sections.campaigns.new-campaign.wizard-audiences.forecast.audiences-info'
                                    )}
                                </span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                                <Icon
                                    name="info-circle"
                                    className="text-blue-500"
                                />
                                <span>
                                    {t(
                                        'sections.campaigns.new-campaign.wizard-screens.screens.details-hint'
                                    )}
                                </span>
                            </div>
                        </div>
                        {hasSelectedScreens ? (
                            <div className="text-sm whitespace-nowrap md:text-right">
                                <DownloadScreensToCSV
                                    userMargin={userMargin}
                                    screensState={screensState}
                                />
                            </div>
                        ) : null}
                    </div>
                    <span className="block text-sm font-medium text-gray-700">
                        {t(
                            'sections.campaigns.new-campaign.wizard-screens.label'
                        )}{' '}
                        <RequiredTag alert={errors.selectedScreens} />
                    </span>
                    <div
                        className="h-full"
                        onMouseDownCapture={markSelectionIntentFromEvent}
                        onKeyDownCapture={markSelectionIntentFromEvent}>
                        <DataTable
                            responsive={true}
                            columns={columns}
                            fixedHeader={true}
                            fixedHeaderScrollHeight={() => calculateHeight()}
                            subHeader={false}
                            selectableRows
                            selectableRowsNoSelectAll={false}
                            selectableRowsHighlight
                            selectableRowSelected={(row) =>
                                isScreenSelected(row)
                            }
                            scrollCollapse
                            keyField="__screenKey"
                            data={tableData}
                            pagination
                            paginationServer={!showSelectedScreens}
                            paginationDefaultPage={
                                showSelectedScreens ? 1 : page
                            }
                            paginationResetDefaultPage={paginationReset}
                            paginationTotalRows={
                                showSelectedScreens
                                    ? tableData.length
                                    : totalRows
                            }
                            paginationPerPage={perPage}
                            onChangePage={onChangePage}
                            onChangeRowsPerPage={onChangePerPage}
                            sortServer
                            onSort={handleSort}
                            defaultSortFieldId={orderBy}
                            defaultSortAsc={order !== 'desc'}
                            paginationRowsPerPageOptions={[50, 100, 200, 300]}
                            paginationIconLastPage={false}
                            paginationIconFirstPage={false}
                            paginationServerOptions={{
                                persistSelectedOnPageChange: false,
                                persistSelectedOnSort: false,
                            }}
                            paginationComponentOptions={{
                                rangeSeparatorText: t('common.words.of'),
                                rowsPerPageText: t(
                                    'common.components.react-data-table.screens-per-page'
                                ),
                                selectAllRowsItemText: t(
                                    'common.components.react-data-table.select-all-screens'
                                ),
                            }}
                            progressPending={
                                loadingDataTable ||
                                isFetchingData
                            }
                            progressComponent={
                                <>
                                    {t(
                                        'sections.campaigns.new-campaign.wizard-screens.screens.loading-screens'
                                    )}
                                </>
                            }
                            noDataComponent={t(
                                'common.components.react-data-table.no-screens'
                            )}
                            conditionalRowStyles={conditionalRowStyles}
                            onSelectedRowsChange={handleSelectedRowsChange}
                        />
                    </div>
                </div>
            </div>
        </>
    );
});

WizardScreens.propTypes = {
    newCampaign: PropTypes.object.isRequired,
    selectedCurrencyCode: PropTypes.string,
    isResizeMapOn: PropTypes.bool,

    rows: PropTypes.array,
    totalRows: PropTypes.number,
    page: PropTypes.number,
    perPage: PropTypes.number,

    selectedScreens: PropTypes.array,
    onChangeSelectedScreens: PropTypes.func,

    filters: PropTypes.shape({
        providerSource: PropTypes.oneOfType([
            PropTypes.arrayOf(PropTypes.string),
            PropTypes.string,
        ]),
        q: PropTypes.string,
        selectedPublishers: PropTypes.array,
        selectedScreenTypes: PropTypes.array,
        selectedTiers: PropTypes.array,
        showSelectedScreens: PropTypes.bool,
        maxCpm: PropTypes.oneOfType([
            PropTypes.number,
            PropTypes.oneOf([null]),
        ]),
        isFilterByAudiences: PropTypes.bool,
        selectedVenueTypes: PropTypes.array,
        selectedRotations: PropTypes.array,
        selectedSlotDurations: PropTypes.array,
    }),

    onFiltersChange: PropTypes.func,
    onChangePage: PropTypes.func,
    onChangePerPage: PropTypes.func,

    userMargin: PropTypes.number,
    loadingDataTable: PropTypes.bool,
    isFetchingData: PropTypes.bool,

    screenOpened: PropTypes.object,
    setScreenOpened: PropTypes.func,

    filteredScreensLocal: PropTypes.array,
    screensMatchingFilters: PropTypes.array,
    screenPassesFilters: PropTypes.func,
    screenPassesSelectionFilters: PropTypes.func,
    availableVenueTypeIds: PropTypes.array,
    availableVenueTypeCounts: PropTypes.array,
    availableRotations: PropTypes.array,
    availableRotationCounts: PropTypes.array,
    availableSlotDurations: PropTypes.array,
    availableSlotDurationCounts: PropTypes.array,
    availablePublisherIds: PropTypes.array,
    availablePublisherCounts: PropTypes.array,
    availableScreensForImport: PropTypes.array,
    filterScreenByPoi: PropTypes.bool,
    setFilterScreenByPoi: PropTypes.func,
    selectedPoiZoneKeys: PropTypes.instanceOf(Set),
    onPoiZoneKeysChange: PropTypes.func,
    tiers: PropTypes.array,
};

export default WizardScreens;
